import { useEffect } from 'react';

export const SITE_NAME = 'WE R GAME';
export const DEFAULT_TITLE = 'WE R GAME | Sports Information Markets & Fan Gaming';
export const DEFAULT_DESCRIPTION =
  'WE R GAME is a sports information markets and fan gaming platform. Play FREE, BOOST and MARKET modes, follow live sports, earn Golden Tickets and compete for rewards.';
export const SITE_URL = String(process.env.REACT_APP_SITE_URL || 'https://wergame.io').replace(/\/$/, '');
export const DEFAULT_OG_IMAGE = `${SITE_URL}/logo512.png`;
export const DEFAULT_KEYWORDS = [
  'WE R GAME',
  'WeRGame',
  'WeRgame',
  'wergame',
  'sports information markets',
  'fan gaming',
  'sports prediction',
  'FREE BOOST MARKET',
  'Golden Tickets',
  'live sports',
  'sports markets',
].join(', ');

export function pageTitle(pageName) {
  const name = String(pageName || '').trim();
  if (!name) return DEFAULT_TITLE;
  if (name.includes(SITE_NAME) || name.includes('|')) return name;
  return `${name} | ${SITE_NAME}`;
}

export function absoluteUrl(path = '/') {
  if (!path) return `${SITE_URL}/`;
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

function upsertMeta(selector, attributes) {
  if (typeof document === 'undefined') return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attributes).forEach(([key, value]) => {
    if (value != null) el.setAttribute(key, value);
  });
}

function upsertLink(rel, href) {
  if (typeof document === 'undefined' || !href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(data) {
  if (typeof document === 'undefined') return;
  let el = document.getElementById('wrg-jsonld');
  if (!el) {
    el = document.createElement('script');
    el.id = 'wrg-jsonld';
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function applySeoTags({
  title,
  description,
  path,
  image,
  type = 'website',
  noIndex = false,
} = {}) {
  if (typeof document === 'undefined') return;

  const fullTitle = pageTitle(title);
  const desc = String(description || DEFAULT_DESCRIPTION).slice(0, 320);
  const url = absoluteUrl(path || window.location.pathname || '/');
  const img = image ? absoluteUrl(image) : DEFAULT_OG_IMAGE;
  const robots = noIndex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  document.title = fullTitle;

  upsertMeta('meta[name="description"]', { name: 'description', content: desc });
  upsertMeta('meta[name="keywords"]', { name: 'keywords', content: DEFAULT_KEYWORDS });
  upsertMeta('meta[name="robots"]', { name: 'robots', content: robots });
  upsertMeta('meta[name="googlebot"]', { name: 'googlebot', content: robots });
  upsertMeta('meta[name="author"]', { name: 'author', content: SITE_NAME });
  upsertMeta('meta[name="application-name"]', { name: 'application-name', content: SITE_NAME });
  upsertMeta('meta[name="apple-mobile-web-app-title"]', {
    name: 'apple-mobile-web-app-title',
    content: SITE_NAME,
  });
  upsertMeta('meta[name="theme-color"]', { name: 'theme-color', content: '#b91c1c' });

  upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
  upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
  upsertMeta('meta[property="og:title"]', { property: 'og:title', content: fullTitle });
  upsertMeta('meta[property="og:description"]', { property: 'og:description', content: desc });
  upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });
  upsertMeta('meta[property="og:image"]', { property: 'og:image', content: img });
  upsertMeta('meta[property="og:locale"]', { property: 'og:locale', content: 'en_US' });

  upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
  upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: fullTitle });
  upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: desc });
  upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: img });

  upsertLink('canonical', url);

  upsertJsonLd({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: SITE_NAME,
        alternateName: ['WeRGame', 'WeRgame', 'wergame'],
        url: `${SITE_URL}/`,
        logo: DEFAULT_OG_IMAGE,
        description: DEFAULT_DESCRIPTION,
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: SITE_NAME,
        alternateName: ['WeRGame', 'WeRgame', 'wergame'],
        url: `${SITE_URL}/`,
        description: DEFAULT_DESCRIPTION,
        publisher: { '@id': `${SITE_URL}/#organization` },
        inLanguage: 'en-US',
      },
      {
        '@type': 'WebPage',
        '@id': `${url}#webpage`,
        url,
        name: fullTitle,
        description: desc,
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: { '@id': `${SITE_URL}/#organization` },
        inLanguage: 'en-US',
      },
    ],
  });
}

const ROUTE_SEO = [
  { test: (p) => p === '/' || p === '', title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION },
  {
    test: (p) => p.startsWith('/leaderboard'),
    title: 'Leaderboard',
    description:
      'See top WE R GAME players. Climb the leaderboard across FREE, BOOST and MARKET sports information markets.',
  },
  {
    test: (p) => p.startsWith('/streaks'),
    title: 'Streaks',
    description:
      'Build daily play streaks on WE R GAME and earn Golden Tickets as you compete in sports information markets.',
  },
  {
    test: (p) => p.startsWith('/jackpot'),
    title: 'Jackpots',
    description:
      'Compete for WE R GAME jackpots. Play FREE, BOOST and MARKET and follow live sports for fan gaming rewards.',
  },
  {
    test: (p) => p === '/store' || p.startsWith('/store/product'),
    title: 'Golden Store',
    description:
      'Spend Golden Tickets in the WE R GAME store. Exclusive fan gaming rewards for sports information market players.',
  },
  {
    test: (p) => p === '/blogs',
    title: 'News & Insights',
    description:
      'Latest sports information market news, fan gaming insights, and WE R GAME updates.',
  },
  {
    test: (p) => p.startsWith('/privacy'),
    title: 'Privacy Policy',
    description: 'How WE R GAME collects, uses, and protects your information.',
  },
  {
    test: (p) => p.startsWith('/terms'),
    title: 'Terms of Service',
    description: 'Terms for using WE R GAME sports information markets and fan gaming.',
  },
  {
    test: (p) => p.startsWith('/responsible-gaming'),
    title: 'Responsible Gaming',
    description: 'Play WE R GAME responsibly. Tips and tools for sports information markets and fan gaming.',
  },
  {
    test: (p) => p.startsWith('/cookies'),
    title: 'Cookie Policy',
    description: 'How WE R GAME uses cookies and similar technologies.',
  },
  {
    test: (p) => p.startsWith('/referral'),
    title: 'Referral',
    description: 'Invite fans to WE R GAME and earn Golden Tickets for verified players.',
  },
  {
    test: (p) => p.startsWith('/cup/'),
    title: 'Tournament',
    description:
      'Follow live sports tournaments on WE R GAME. Play FREE, BOOST and MARKET across every cup.',
  },
  {
    test: (p) => /\/match\/[^/]+\/boost/.test(p) || /\/poll\/[^/]+\/boost/.test(p),
    title: 'Boost',
    description:
      'BOOST on WE R GAME — stake USDC in sports information markets, earn Golden Tickets, and compete for rewards.',
  },
  {
    test: (p) => /\/match\/[^/]+\/market/.test(p) || /\/poll\/[^/]+\/market/.test(p),
    title: 'Market',
    description:
      'Trade MARKET shares on WE R GAME sports information markets. Follow live sports and compete for rewards.',
  },
  {
    test: (p) => /\/match\/[^/]+\/free/.test(p) || /\/poll\/[^/]+\/free/.test(p),
    title: 'Free Play',
    description:
      'Play FREE on WE R GAME. Fan gaming with tickets, live sports, Golden Tickets, and jackpot rewards.',
  },
  {
    test: (p) => p.startsWith('/blog/'),
    title: 'Article',
    description: 'Sports information markets and fan gaming insights from WE R GAME.',
    type: 'article',
  },
];

const NOINDEX_PREFIXES = [
  '/admin',
  '/superadmin',
  '/profile',
  '/wallet',
  '/notifications',
  '/store/checkout',
  '/store/cart',
  '/store/orders',
  '/login',
  '/unauthorized',
];

export function seoForPath(pathname) {
  const p = pathname || '/';
  const noIndex = NOINDEX_PREFIXES.some((prefix) => p === prefix || p.startsWith(`${prefix}/`));
  const match = ROUTE_SEO.find((row) => row.test(p));
  return {
    title: match?.title || DEFAULT_TITLE,
    description: match?.description || DEFAULT_DESCRIPTION,
    path: p,
    type: match?.type || 'website',
    noIndex,
  };
}

/** Override title/description after page data loads (blogs, matches, cups). */
export function usePageSeo({ title, description, image, type, noIndex } = {}) {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const fallback = seoForPath(window.location.pathname);
    applySeoTags({
      title: title || fallback.title,
      description: description || fallback.description,
      path: window.location.pathname,
      image,
      type: type || fallback.type,
      noIndex: noIndex ?? fallback.noIndex,
    });
    return undefined;
  }, [title, description, image, type, noIndex]);
}
